import React, { Component } from 'react'
import { SketchPicker } from 'react-color'
import Button from '../Button/Button'
import ButtonGroup from '../Button/ButtonGroup/ButtonGroup'
import Icon from '../Icon2/Icon'
import { ScreenColorPickIcon } from './ScreenColorPickIcon'
import {
  COLOR_PICKER_NO_COLOR,
  deriveOpaqueHexFromSketch,
  hexLookupKey,
  isColorPickerNoColor,
  pickInitialColor,
  presetNoColorClick,
  type SketchOnChangePayload
} from './colorPickerHelper'
import { NO_COLOR_TEMPLATE_SWATCH_DATA_URI } from './colorPickerConstants'
import { isScreenColorPickSupported, pickColorFromScreen } from './screenColorPicker'
import Tooltip from '../Tooltip/Tooltip'
import './ColorPicker.css'

export { COLOR_PICKER_NO_COLOR, isColorPickerNoColor }

const LEGACY_TEMPLATE_PRESETS = [
  { color: '#FDBAB1', title: 'Pink' },
  { color: '#FA6E33', title: 'Orange' },
  { color: '#FFD600', title: 'Yellow' },
  { color: '#52C0FF', title: 'Blue' },
  { color: '#01977C', title: 'Green' },
  { color: '#6C5CE7', title: 'Purple' },
  { color: '#002C53', title: 'Ferrario' }
] as const

export type ColorPickerProps = {
  onChange?: Function
  value: string
  testId?: string
  openColorPicker?: boolean
  onClose?: Function
  /** When true, adds the No color template and none/transparent value handling; default false preserves legacy behavior. */
  includeNoColor?: boolean
  /** When true, shows an eyedropper control to sample a color from anywhere on screen when the browser supports it. Default false. */
  enableScreenColorPick?: boolean
}

class ColorPicker extends Component<ColorPickerProps> {
  public static propTypes: any = {}

  state = {
    displayColorPicker: this.props.openColorPicker || false,
    color: pickInitialColor(this.props.includeNoColor, this.props.value),
    tempColor: pickInitialColor(this.props.includeNoColor, this.props.value),
    isPickingFromScreen: false
  }

  handleClick = () => {
    this.setState({ displayColorPicker: !this.state.displayColorPicker })
  }

  handleClose = () => {
    this.setState({ displayColorPicker: false })
    if (this.props.onClose) {
      this.props.onClose()
    }
  }

  handleChange = (color: SketchOnChangePayload) => {
    const includeNoColor = this.props.includeNoColor
    if (!includeNoColor) {
      this.setState({ tempColor: color.hex })
      return
    }
    if (presetNoColorClick(color)) {
      this.setState({ tempColor: COLOR_PICKER_NO_COLOR })
      return
    }
    const opaque = deriveOpaqueHexFromSketch(color)
    const hexLc = typeof color.hex === 'string' ? color.hex.trim().toLowerCase() : ''
    const next =
      opaque !== null
        ? opaque
        : hexLc !== '' && hexLc !== 'transparent'
          ? color.hex
          : null
    if (next !== null) {
      this.setState({ tempColor: next })
    }
  }

  handleChoose = () => {
    this.setState({
      color: this.state.tempColor,
      displayColorPicker: false
    })
    if (this.props.onChange) {
      this.props.onChange(this.state.tempColor)
    }
  }

  applyScreenPickedHex = (hex: string) => {
    this.setState({ tempColor: hex })
  }

  handleScreenColorPick = async () => {
    if (this.state.isPickingFromScreen) {
      return
    }
    this.setState({ isPickingFromScreen: true })
    try {
      const hex = await pickColorFromScreen()
      if (hex) {
        this.applyScreenPickedHex(hex)
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return
      }
    } finally {
      this.setState({ isPickingFromScreen: false })
    }
  }

  colorObj = {
    '#fdbab1': 'Pink',
    '#fa6e33': 'Orange',
    '#ffd600': 'Yellow',
    '#52c0ff': 'Blue',
    '#01977c': 'Green',
    '#6c5ce7': 'Purple',
    '#002c53': 'Ferrario'
  }

  render() {
    const { testId = 'cs-color-picker' } = this.props
    const includeNoColor = this.props.includeNoColor || false
    const presetColors = includeNoColor
      ? [{ color: 'transparent', title: 'No color' }, ...LEGACY_TEMPLATE_PRESETS]
      : [...LEGACY_TEMPLATE_PRESETS]

    const selectedTemplateClass = includeNoColor
      ? isColorPickerNoColor(this.state.tempColor)
        ? 'No_color'
        : this.colorObj[hexLookupKey(this.state.tempColor)] || ''
      : this.colorObj[this.state.tempColor]
        ? this.colorObj[this.state.tempColor]
        : ''

    const showNoColorSwatch = includeNoColor && isColorPickerNoColor(this.state.color)

    const pickerColor =
      includeNoColor && isColorPickerNoColor(this.state.tempColor) ? 'transparent' : this.state.tempColor

    const enableScreenColorPick = this.props.enableScreenColorPick === true
    const showScreenColorPick = enableScreenColorPick && isScreenColorPickSupported()

    return (
      <div className="ColorPicker__wrapper" data-test-id={testId}>
        {includeNoColor ? (
          <div
            onClick={this.handleClick}
            className={`ColorPicker${showNoColorSwatch ? ' ColorPicker--no-color' : ''}`}
            style={showNoColorSwatch ? undefined : { backgroundColor: this.state.color }}
            data-test-id={`${testId}-trigger`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                this.handleClick()
              }
            }}
          >
            {showNoColorSwatch ? (
              <Icon
                icon="NoFill"
                version="v2"
                size="small"
                className="ColorPicker__no-color-icon"
                aria-hidden
                testId={`${testId}-no-color-icon`}
              />
            ) : null}
          </div>
        ) : (
          <div onClick={this.handleClick} className="ColorPicker" style={{ backgroundColor: this.state.color }}></div>
        )}
        {this.state.displayColorPicker ? (
          <div className="ColorPicker__modal">
            <div className="ColorPicker__popover">
              <div className="ColorPicker__heading ColorPicker__heading--custom">Select From Template</div>
              <div className="ColorPicker__layout" onClick={this.handleClose}></div>
              <div
                className={`${selectedTemplateClass ? `Selected_${selectedTemplateClass}` : ''}`}
                style={
                  includeNoColor
                    ? ({
                        ['--color-picker-no-color-swatch' as string]: `url("${NO_COLOR_TEMPLATE_SWATCH_DATA_URI}")`,
                      } as React.CSSProperties)
                    : undefined
                }
              >
                <SketchPicker
                  className="ColorPicker__sketch-picker"
                  color={pickerColor}
                  disableAlpha={true}
                  width="264px"
                  presetColors={presetColors}
                  onChange={this.handleChange}
                />
              </div>
              <div className="ColorPicker__heading ColorPicker__heading--template">Select Custom</div>
              <div className="ColorPicker__hex-code">
                {includeNoColor && isColorPickerNoColor(this.state.tempColor) ? 'NONE' : this.state.tempColor}
              </div>
              <div
                className={
                  showScreenColorPick
                    ? 'ColorPicker__screen-pick-slot'
                    : 'ColorPicker__screen-pick-slot ColorPicker__screen-pick-slot--hidden'
                }
                aria-hidden={!showScreenColorPick}
                hidden={!showScreenColorPick}
              >
                <Tooltip
                  content="Pick color from screen"
                  position="top"
                  variantType="basic"
                  disabled={!showScreenColorPick}
                  testId={`${testId}-screen-pick-tooltip`}
                  wrapperElementType="span"
                >
                  <button
                    type="button"
                    className="ColorPicker__screen-pick"
                    disabled={!showScreenColorPick || this.state.isPickingFromScreen}
                    tabIndex={showScreenColorPick ? 0 : -1}
                    onClick={this.handleScreenColorPick}
                    id="pick-color-from-screen"
                    aria-label="Pick color from screen"
                    data-test-id={`${testId}-screen-pick`}
                  >
                    <ScreenColorPickIcon className="ColorPicker__screen-pick-icon" data-test-id={`${testId}-screen-pick-icon`} />
                  </button>
                </Tooltip>
              </div>
              <div className="ColorPicker__actions">
                <ButtonGroup>
                  <Button buttonType="light" onClick={this.handleClose} version="v2" size="small" testId='cms-click-btn-secondary-action-environments-cancel-choose-color'>
                    Cancel
                  </Button>
                  <Button buttonType="secondary" onClick={this.handleChoose} version="v2" size="small" testId='cms-click-btn-secondary-action-environments-choose-color'>
                    Choose
                  </Button>
                </ButtonGroup>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    )
  }
}

export default ColorPicker
